import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";

import type { WikiSummary } from "@/api/db/wiki";
import { format } from "@/lib/format";
import { WikiLabels } from "@/lib/labels/wiki";
import { cn } from "@/lib/utils";

function fold(value: string) {
	return value
		.normalize("NFD")
		.replaceAll(/\p{Diacritic}/gu, "")
		.toLowerCase();
}

export function WikiIndex({
	pages,
	activeSlug,
	onCreate,
	onNavigate,
}: {
	pages: WikiSummary[];
	activeSlug: string | null;
	onCreate: () => void;
	onNavigate: () => void;
}) {
	const [search, setSearch] = useState("");

	const term = fold(search.trim());
	const visible = term ? pages.filter((page) => fold(page.title).includes(term)) : pages;

	const emptyMessage = pages.length === 0 ? WikiLabels.emptyIndex : WikiLabels.noMatch;

	return (
		<div className="surface-panel p-3">
			<button
				type="button"
				onClick={onCreate}
				className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:h-auto md:py-1.5"
			>
				<Plus className="size-4" />
				{WikiLabels.newPage}
			</button>

			<input
				type="search"
				value={search}
				onChange={(event) => setSearch(event.target.value)}
				placeholder={WikiLabels.searchPlaceholder}
				className="mt-2 control-field h-11 md:h-10"
			/>

			{visible.length === 0 && (
				<p className="px-2 py-4 text-center text-xs italic text-muted-foreground">{emptyMessage}</p>
			)}

			{visible.length > 0 && (
				<nav className="mt-2 flex flex-col gap-0.5">
					{visible.map((page) => (
						<Link
							key={page.slug}
							to="/wiki/$slug"
							params={{ slug: page.slug }}
							onClick={onNavigate}
							className={cn(
								"min-w-0 rounded-md px-2.5 py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:py-2",
								page.slug === activeSlug ? "bg-primary/10" : "hover:bg-background",
							)}
						>
							<span
								className={cn(
									"block truncate font-display text-sm font-medium",
									page.slug === activeSlug ? "text-primary" : "text-foreground",
								)}
							>
								{page.title}
							</span>
							<span className="flex items-baseline gap-2 text-xs text-muted-foreground">
								<span className="truncate">{page.updated_by}</span>
								<span className="shrink-0 font-mono">{format.dateTime(page.updated_at)}</span>
							</span>
						</Link>
					))}
				</nav>
			)}
		</div>
	);
}
